"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.FileUploadManager = void 0;
const sdk_geometry_api_sdk_v2_1 = require("@shapediver/sdk.geometry-api-sdk-v2");
const viewer_shared_services_1 = require("@shapediver/viewer.shared.services");
const FileParameter_1 = require("../dto/FileParameter");
/**
 * Manager responsible for file uploads.
 *
 * The manager is created by the SessionEngineCore and can be accessed
 * via the `fileUploadManager` property.
 */
class FileUploadManager {
    constructor(sessionEngineCore) {
        this._sessionEngineCore = sessionEngineCore;
    }
    get canUploadGLTF() {
        try {
            this._sessionEngineCore.utilsManager.checkAvailability("gltf-upload");
            return true;
        }
        catch (e) {
            return false;
        }
    }
    /**
     * Retrieves file information for a given parameter and file ID.
     *
     * @param parameterId The ID of the parameter
     * @param fileId The ID of the file
     * @param retry Whether to retry the request in case of failure
     * @returns Promise with the file information
     */
    getFileInfo(parameterId, fileId, retry = false) {
        return __awaiter(this, void 0, void 0, function* () {
            this._sessionEngineCore.utilsManager.checkAvailability();
            try {
                const response = yield new sdk_geometry_api_sdk_v2_1.FileApi(this._sessionEngineCore.sdkConfig).getFileMetadata(this._sessionEngineCore.sessionId, parameterId, fileId);
                const { size, filename } = (0, sdk_geometry_api_sdk_v2_1.extractFileInfo)(response.headers);
                return {
                    parameterId: parameterId,
                    id: fileId,
                    size: size,
                    filename: filename,
                };
            }
            catch (e) {
                yield this._sessionEngineCore.utilsManager.handleError(e, retry);
                return yield this.getFileInfo(parameterId, fileId, true);
            }
        });
    }
    /**
     * Get all file parameters from the parameter set.
     * If the parameter is not set in the parameter set, the value from the parameter object is used.
     *
     * @param parameters
     * @returns
     */
    getFileParameterSet(parameters) {
        const fileParameterSet = {};
        for (const parameterId in this._sessionEngineCore.parameterManager
            .parameters) {
            if (this._sessionEngineCore.parameterManager.parameters[parameterId] instanceof FileParameter_1.FileParameter) {
                fileParameterSet[parameterId] =
                    parameters[parameterId] !== undefined
                        ? parameters[parameterId]
                        : this._sessionEngineCore.parameterManager
                            .parameters[parameterId].value;
            }
        }
        return fileParameterSet;
    }
    /**
     * Uploads a file for a given parameter.
     *
     * @param parameterId The ID of the parameter
     * @param data The file to upload
     * @param type The MIME type of the file
     * @param retry Whether to retry the request in case of failure
     * @returns Promise with the file ID
     */
    uploadFile(parameterId, data, type, retry = false) {
        return __awaiter(this, void 0, void 0, function* () {
            this._sessionEngineCore.utilsManager.checkAvailability("file-upload");
            try {
                const result = (yield new sdk_geometry_api_sdk_v2_1.FileApi(this._sessionEngineCore.sdkConfig).uploadFile(this._sessionEngineCore.sessionId, {
                    [parameterId]: {
                        size: data.size,
                        format: type,
                        filename: data.name === "" ? undefined : data.name,
                    },
                })).data;
                if (result &&
                    result.asset &&
                    result.asset.file &&
                    result.asset.file[parameterId]) {
                    const fileAsset = result.asset.file[parameterId];
                    yield new sdk_geometry_api_sdk_v2_1.UtilsApi(this._sessionEngineCore.sdkConfig).uploadAsset(fileAsset.href, yield data.arrayBuffer(), fileAsset.headers);
                    return fileAsset.id;
                }
                else {
                    throw new viewer_shared_services_1.ShapeDiverViewerSessionError("Session.uploadFile: Upload reply has not the required format.");
                }
            }
            catch (e) {
                yield this._sessionEngineCore.utilsManager.handleError(e, retry);
                return yield this.uploadFile(parameterId, data, type, true);
            }
        });
    }
    /**
     * Uploads all file parameters and returns the file parameter values.
     * If parameterValues is provided, the file parameter values are added to it.
     *
     * @param parameterValues
     * @returns
     */
    uploadFileParameters(parameterValues) {
        return __awaiter(this, void 0, void 0, function* () {
            const parameterValueSet = parameterValues !== undefined
                ? this.getFileParameterSet(parameterValues)
                : undefined;
            const fileParameterValues = {};
            // load file parameter first
            for (const parameterId in this._sessionEngineCore.parameterManager
                .parameters) {
                if (this._sessionEngineCore.parameterManager.parameters[parameterId] instanceof FileParameter_1.FileParameter) {
                    fileParameterValues[parameterId] = yield (this._sessionEngineCore.parameterManager.parameters[parameterId]).upload(parameterValueSet
                        ? parameterValueSet[parameterId]
                        : undefined);
                    if (parameterValues &&
                        parameterValues[parameterId] !== undefined) {
                        // if a value was provided, we replace it with the returned id
                        parameterValues[parameterId] =
                            fileParameterValues[parameterId];
                        // if the parameter value of the file parameter was used, set the value to the parameter
                        if (this._sessionEngineCore.parameterManager.parameters[parameterId].value !== fileParameterValues[parameterId])
                            this._sessionEngineCore.parameterManager.parameters[parameterId].value = fileParameterValues[parameterId];
                    }
                    else if (this._sessionEngineCore.parameterManager.parameters[parameterId].value !== fileParameterValues[parameterId]) {
                        this._sessionEngineCore.parameterManager.parameters[parameterId].value = fileParameterValues[parameterId];
                    }
                }
            }
            return fileParameterValues;
        });
    }
    /**
     * Uploads a GLTF file to the session.
     *
     * @param blob The GLTF file as a Blob
     * @param conversion The type of conversion to apply
     * @param retry Whether to retry the request in case of failure
     * @returns Promise with the response data
     */
    uploadGLTF(blob, conversion = sdk_geometry_api_sdk_v2_1.QueryGltfConversion.NONE, retry = false) {
        return __awaiter(this, void 0, void 0, function* () {
            this._sessionEngineCore.utilsManager.checkAvailability("gltf-upload");
            try {
                const responseDto = (yield new sdk_geometry_api_sdk_v2_1.GltfApi(this._sessionEngineCore.sdkConfig).uploadGltf(this._sessionEngineCore.sessionId, new File([blob], "model.gltf", {
                    type: "model/gltf-binary",
                }), conversion)).data;
                if (!responseDto || !responseDto.gltf || !responseDto.gltf.href)
                    throw new viewer_shared_services_1.ShapeDiverViewerSessionError("Session.uploadGLTF: Upload reply has not the required format.");
                return responseDto;
            }
            catch (e) {
                yield this._sessionEngineCore.utilsManager.handleError(e, retry);
                return yield this.uploadGLTF(blob, conversion, true);
            }
        });
    }
    /**
     * Uploads SDTF files to the session.
     *
     * @param arrayBuffers The SDTF files as ArrayBuffers
     * @param retry Whether to retry the request in case of failure
     * @returns Promise with the response data
     */
    uploadSDTF(arrayBuffers, retry = false) {
        return __awaiter(this, void 0, void 0, function* () {
            this._sessionEngineCore.utilsManager.checkAvailability("file-upload");
            try {
                const responseDto = (yield new sdk_geometry_api_sdk_v2_1.SdtfApi(this._sessionEngineCore.sdkConfig).uploadSdtf(this._sessionEngineCore.sessionId, arrayBuffers.map((arrayBuffer) => {
                    return {
                        namespace: "pub",
                        content_length: arrayBuffer.byteLength,
                        content_type: sdk_geometry_api_sdk_v2_1.ReqSdtfType.MODEL_SDTF,
                    };
                }))).data;
                if (!responseDto ||
                    !responseDto.asset ||
                    !responseDto.asset.sdtf ||
                    responseDto.asset.sdtf.length !== arrayBuffers.length)
                    throw new viewer_shared_services_1.ShapeDiverViewerSessionError("Session.uploadSDTF: Upload reply has not the required format.");
                const promises = arrayBuffers.map((buffer, index) => {
                    const url = responseDto.asset.sdtf[index].href;
                    return new sdk_geometry_api_sdk_v2_1.UtilsApi(this._sessionEngineCore.sdkConfig).upload(url, buffer, sdk_geometry_api_sdk_v2_1.ReqSdtfType.MODEL_SDTF);
                });
                yield Promise.all(promises);
                return responseDto.asset.sdtf;
            }
            catch (e) {
                yield this._sessionEngineCore.utilsManager.handleError(e, retry);
                return yield this.uploadSDTF(arrayBuffers, true);
            }
        });
    }
}
exports.FileUploadManager = FileUploadManager;
//# sourceMappingURL=FileUploadManager.js.map